import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { CSVLink } from "react-csv";
import ReactDatePicker from "react-datepicker";
import { Link, Outlet, useLoaderData, useLocation } from "react-router-dom";
import Select from "react-select";

import { store } from "../..";
import { alertActions } from "../../features/alert/alertSlice";
import {
    globalActions,
    selectSetting,
} from "../../features/global/globalSlice";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { getOrders, getProductYears, updateOrder } from "../../lib/api";
import {
    capitalize,
    currencyFormat,
    defaultDateFormat,
    defaultDateTimeFormat,
} from "../../lib/utils";
import { IOrder, IOrderProduct } from "../../types/api";

interface Filters {
    ageLevels: {
        options: Array<string>;
        filter: Array<string> | undefined;
    };
    league: {
        options: Array<string>;
        filter:
            | {
                  value: string;
                  label: string;
              }
            | undefined;
    };
    user?: string;
    team?: string;
    orderDateStart?: Date;
    orderDateEnd?: Date;
    orderCompletionStart?: Date;
    orderCompletionEnd?: Date;
    statuses: {
        options: Array<string>;
        filter: Array<string | undefined> | undefined;
    };
}

interface CSVData {
    orderId: number;
    orderDate: string;
    status: "init" | "pending" | "processing" | "completed" | "cancelled";
    paidDate: string;
    name: string;
    email: string;
    teamName: string;
    ageLevel: string;
    league: string;
    amount: string;
    paymentType: "Check" | "Credit Card";
}

export const getOrderAmount = (o: IOrder): number =>
    Number(o.total) -
    (Number(o.discount) || 0) -
    (Number(o.checkDiscount) || 0);

export const loader = async () => {
    const { settings } = store.getState().global;
    const promises = [];

    !settings.length && (await store.dispatch(globalActions.getSettings()));

    const currentYear = selectSetting(
        store.getState(),
        "majorybl_current_league_year",
    )?.setting;

    const [{ years }, { orders }] = await Promise.all([
        getProductYears(),
        getOrders(parseInt(currentYear)),
    ]);

    return {
        years,
        orders,
        currentYear: parseInt(currentYear),
    };
};

const LeagueRegistrations = (): React.JSX.Element | undefined => {
    const {
        years,
        orders: loadedOrders,
        currentYear: loadedCurrentYear,
    } = useLoaderData() as {
        years: number[];
        orders: IOrder[];
        currentYear: number;
    };

    const [orders, setOrders] = useState<IOrder[]>(loadedOrders);
    const [currentYear, setCurrentYear] = useState<number>(loadedCurrentYear);

    const [totals, setTotals] = useState<{
        total: number;
        unpaid: number;
        count: number;
    }>({
        total: 0,
        unpaid: 0,
        count: 0,
    });

    const location = useLocation();
    const isOnRegPage = /league-management\/league-registrations\/?$/.test(
        location.pathname,
    );
    const dispatch = useAppDispatch();

    const [csvData, setCSVData] = useState<Array<CSVData>>([]);
    const CSVHeaders = [
        { label: "Order Id", key: "orderId" },
        { label: "Order Date", key: "orderDate" },
        { label: "Order Status", key: "status" },
        { label: "Paid Date", key: "paidDate" },
        { label: "Name", key: "name" },
        { label: "Email", key: "email" },
        { label: "Team Name", key: "teamName" },
        { label: "Age Level", key: "ageLevel" },
        { label: "League", key: "league" },
        { label: "Amount", key: "amount" },
        { label: "Payment Type", key: "paymentType" },
    ];

    const defaultStatusFilters = Array.from(
        new Set(
            orders.map(o => {
                return o.status !== "cancelled" ? o.status : undefined;
            }),
        ),
    );

    const [filters, setFilters] = useState<Filters>({
        ageLevels: {
            options: Array.from(
                new Set(
                    orders.flatMap(o =>
                        o.products ? o.products.map(p => p.name) : [],
                    ),
                ),
            ).sort((a, b) => {
                return parseInt(a) - parseInt(b);
            }),
            filter: undefined,
        },
        league: {
            options: Array.from(
                new Set(
                    orders.flatMap(o =>
                        o.products ? o.products.map(p => p.season) : [],
                    ),
                ),
            ),
            filter: { value: "Youth", label: "Youth" },
        },
        user: undefined,
        team: undefined,
        orderCompletionStart: undefined,
        orderCompletionEnd: undefined,
        orderDateEnd: undefined,
        orderDateStart: undefined,
        statuses: {
            options: Array.from(new Set(orders.map(o => o.status))).sort(),
            filter: defaultStatusFilters,
        },
    });

    useEffect(() => {
        if (orders.length > 0) {
            const filteredOrders = orders
                .filter(o => o.status !== "cancelled")
                .filter(o =>
                    filters.ageLevels.filter
                        ? o.products.some(p =>
                              filters.ageLevels.filter?.includes(p.name),
                          )
                        : true,
                )
                .filter(o =>
                    filters.league.filter
                        ? o.products.some(
                              p => filters.league.filter?.label === p.season,
                          )
                        : true,
                )
                .filter(o => {
                    return filters.orderDateStart
                        ? new Date(
                              defaultDateFormat.format(new Date(o.date)),
                          ) >=
                              new Date(
                                  defaultDateFormat.format(
                                      filters.orderDateStart,
                                  ),
                              )
                        : true;
                })
                .filter(o => {
                    return filters.orderDateEnd
                        ? new Date(
                              defaultDateFormat.format(new Date(o.date)),
                          ) <=
                              new Date(
                                  defaultDateFormat.format(
                                      filters.orderDateEnd,
                                  ),
                              )
                        : true;
                });

            const total = filteredOrders.reduce(
                (acc, o) => acc + getOrderAmount(o),
                0,
            );
            const unpaid = filteredOrders
                .filter(o => o.status !== "completed")
                .reduce((acc, o) => acc + getOrderAmount(o), 0);
            const count = filteredOrders.reduce((acc, o) => {
                if (o.products && o.products.length > 0) {
                    const filteredProducts = o.products
                        .filter(p =>
                            filters.ageLevels.filter
                                ? filters.ageLevels.filter?.includes(p.name)
                                : true,
                        )
                        .filter(p =>
                            filters.league.filter
                                ? filters.league.filter.label === p.season
                                : true,
                        )
                        .filter(p =>
                            filters.team
                                ? p.meta.teamName
                                    ? p.meta.teamName
                                          .toLowerCase()
                                          .includes(filters.team!.toLowerCase())
                                    : false
                                : true,
                        );
                    return acc + filteredProducts.length;
                }

                return acc;
            }, 0);

            setTotals({ total, unpaid, count });
        } else {
            setTotals({ total: 0, unpaid: 0, count: 0 });
        }
    }, [
        orders,
        filters.orderDateStart,
        filters.orderDateEnd,
        filters.ageLevels.filter,
        filters.league.filter,
        currentYear,
    ]);

    const handleStatusChange = async (
        orderId: IOrder["id"],
        status: IOrder["status"],
    ) => {
        await updateOrder(orderId, status as IOrder["status"])
            .then(response => {
                setOrders(prev =>
                    prev.map(o =>
                        o.id === orderId
                            ? {
                                  ...o,
                                  status: status,
                                  completionDate: new Date().toString(),
                              }
                            : o,
                    ),
                );
                dispatch(
                    alertActions.success({
                        title: `Order updatd successfully`,
                        dismissible: true,
                    }),
                );
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem updating the order.`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    const handleYearChange = async (year: number | null) => {
        if (year) {
            getOrders(year)
                .then(response => {
                    setOrders(response.orders);
                    setCurrentYear(year);
                    setFilters(prev => ({
                        ...prev,
                        statuses: {
                            filter: Array.from(
                                new Set(
                                    response.orders.map(o => {
                                        return o.status !== "cancelled"
                                            ? o.status
                                            : undefined;
                                    }),
                                ),
                            ),
                            options: Array.from(
                                new Set(response.orders.map(o => o.status)),
                            ),
                        },
                    }));
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem fetching orders.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                });
        }
    };

    const generateTableRow = (o: IOrder, p?: IOrderProduct, idx?: number) => {
        return (
            <tr key={idx}>
                <td>{defaultDateTimeFormat.format(new Date(o.date))}</td>
                <td>{o.status === "completed" ? "Paid" : "Unpaid"}</td>
                <td>
                    {o.completionDate ? (
                        defaultDateFormat.format(new Date(o.completionDate))
                    ) : (
                        <Button
                            onClick={() =>
                                handleStatusChange(o.id, "completed")
                            }
                        >
                            Mark Paid
                        </Button>
                    )}
                </td>
                <td>{o.user.name}</td>
                <td>{o.user.email}</td>
                <td>{p && p.meta.teamName}</td>
                <td>{p && p.name}</td>
                <td>
                    {p
                        ? currencyFormat.format(p.orderPrice)
                        : currencyFormat.format(getOrderAmount(o))}
                </td>
                <td>{o.paymentType}</td>
                <td>
                    <Link
                        to={`/league-management/league-registrations/${o.id}`}
                        className="btn btn-primary"
                    >
                        View
                    </Link>
                </td>
            </tr>
        );
    };

    const generateCSVRow = (o: IOrder, p?: IOrderProduct) => {
        return {
            orderId: o.id,
            orderDate: defaultDateTimeFormat.format(new Date(o.date)),
            status: o.status === "completed" ? "Paid" : "Unpaid",
            paidDate:
                o.completionDate &&
                defaultDateTimeFormat.format(new Date(o.completionDate)),
            name: o.user.name,
            email: o.user.email,
            teamName: p && p.meta.teamName,
            ageLevel: p && p.name,
            league: p && p.season,
            amount: p
                ? currencyFormat.format(p.orderPrice)
                : currencyFormat.format(getOrderAmount(o)),
            paymentType: o.paymentType,
        };
    };

    const handleDownload = () => {
        const filteredData = orders
            .filter(o =>
                filters.ageLevels.filter
                    ? o.products.some(p =>
                          filters.ageLevels.filter?.includes(p.name),
                      )
                    : true,
            )
            .filter(o =>
                filters.league.filter
                    ? o.products.some(
                          p => filters.league.filter?.label === p.season,
                      )
                    : true,
            )
            .filter(o =>
                filters.statuses.filter
                    ? filters.statuses.filter.includes(o.status)
                    : true,
            )
            .filter(o => {
                return filters.orderDateStart
                    ? new Date(defaultDateFormat.format(new Date(o.date))) >=
                          new Date(
                              defaultDateFormat.format(filters.orderDateStart),
                          )
                    : true;
            })
            .filter(o => {
                return filters.orderDateEnd
                    ? new Date(defaultDateFormat.format(new Date(o.date))) <=
                          new Date(
                              defaultDateFormat.format(filters.orderDateEnd),
                          )
                    : true;
            })
            .filter(o => {
                return filters.orderCompletionStart
                    ? o.completionDate
                        ? new Date(
                              defaultDateFormat.format(
                                  new Date(o.completionDate),
                              ),
                          ) >=
                          new Date(
                              defaultDateFormat.format(
                                  filters.orderCompletionStart,
                              ),
                          )
                        : false
                    : true;
            })
            .filter(o => {
                return filters.orderCompletionEnd
                    ? o.completionDate
                        ? new Date(
                              defaultDateFormat.format(
                                  new Date(o.completionDate),
                              ),
                          ) <=
                          new Date(
                              defaultDateFormat.format(
                                  filters.orderCompletionEnd,
                              ),
                          )
                        : false
                    : true;
            })
            .filter(o =>
                filters.user
                    ? o.user.email
                          .toLowerCase()
                          .includes(filters.user.toLowerCase()) ||
                      o.user.name
                          .toLowerCase()
                          .includes(filters.user.toLowerCase())
                    : true,
            )
            .filter(o =>
                filters.team
                    ? o.products.some(p =>
                          p.meta.teamName
                              ? p.meta.teamName
                                    .toLowerCase()
                                    .includes(filters.team!.toLowerCase())
                              : false,
                      )
                    : true,
            )
            .sort((a, b) => {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            })
            .flatMap(o =>
                o.products
                    ? o.products
                          .filter(p =>
                              filters.ageLevels.filter
                                  ? filters.ageLevels.filter?.includes(p.name)
                                  : true,
                          )
                          .filter(p =>
                              filters.league.filter
                                  ? filters.league.filter.label === p.season
                                  : true,
                          )
                          .filter(p =>
                              filters.team
                                  ? p.meta.teamName
                                      ? p.meta.teamName
                                            .toLowerCase()
                                            .includes(
                                                filters.team!.toLowerCase(),
                                            )
                                      : false
                                  : true,
                          )
                          .map(p => generateCSVRow(o, p))
                    : generateCSVRow(o),
            )
            .filter((data): data is CSVData => data !== null);

        setCSVData(filteredData);
    };

    return isOnRegPage ? (
        <section
            className="leagueRegistrationsContent"
            data-testid="leagueRegistrationsContent"
        >
            <h2>League Registrations</h2>
            <Row className="registrationFilters">
                <Col xs={12} sm={6} lg={2} className="my-2">
                    <h4>Age Level:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.ageLevels.options.map((a, idx) => {
                            return {
                                value: idx,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                ageLevels: {
                                    ...prev.ageLevels,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={2} className="my-2">
                    <h4>League:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={false}
                        options={filters.league.options.map(l => {
                            return {
                                value: l,
                                label: l,
                            };
                        })}
                        defaultValue={filters.league.filter}
                        value={filters.league.filter}
                        onChange={l => {
                            setFilters(prev => ({
                                ...prev,
                                league: {
                                    ...prev.league,
                                    filter: l || undefined,
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={2} className="my-2">
                    <h4>User:</h4>
                    <Form.Control
                        type="text"
                        placeholder="User name or email"
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                user:
                                    e.target.value.trim() === ""
                                        ? undefined
                                        : e.target.value.trim(),
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={3} className="my-2">
                    <h4>Team Name:</h4>
                    <Form.Control
                        type="text"
                        placeholder="Team name"
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                team:
                                    e.target.value.trim() === ""
                                        ? undefined
                                        : e.target.value.trim(),
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} lg={3} className="my-2">
                    <h4>Order Status:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.statuses.options.map(s => {
                            return {
                                value: s,
                                label: capitalize(s),
                            };
                        })}
                        defaultValue={filters.statuses.filter?.map(s => {
                            return s
                                ? {
                                      value: s,
                                      label: capitalize(s),
                                  }
                                : undefined;
                        })}
                        value={filters.statuses.filter?.map(o => {
                            return o
                                ? {
                                      value: o,
                                      label: capitalize(o),
                                  }
                                : undefined;
                        })}
                        onChange={s => {
                            setFilters(prev => ({
                                ...prev,
                                statuses: {
                                    ...prev.statuses,
                                    filter:
                                        s.length === 0
                                            ? defaultStatusFilters
                                            : s?.map(o => o?.value),
                                },
                            }));
                        }}
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={2} className="my-2">
                    <h4>Order Date Start:</h4>
                    <ReactDatePicker
                        selected={
                            filters.orderDateStart
                                ? new Date(filters.orderDateStart)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                orderDateStart: d || undefined,
                            }))
                        }
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={2} className="my-2">
                    <h4>Order Date End:</h4>
                    <ReactDatePicker
                        selected={
                            filters.orderDateEnd
                                ? new Date(filters.orderDateEnd)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                orderDateEnd: d || undefined,
                            }))
                        }
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={3} className="my-2">
                    <h4>Completion Date Start:</h4>
                    <ReactDatePicker
                        selected={
                            filters.orderCompletionStart
                                ? new Date(filters.orderCompletionStart)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                orderCompletionStart: d || undefined,
                            }))
                        }
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                    />
                </Col>
                <Col xs={12} sm={6} lg={4} xl={3} className="my-2">
                    <h4>Completion Date End:</h4>
                    <ReactDatePicker
                        selected={
                            filters.orderCompletionEnd
                                ? new Date(filters.orderCompletionEnd)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                orderCompletionEnd: d || undefined,
                            }))
                        }
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                    />
                </Col>
                <Col xs={12} sm={6} lg={2} xl={2} className="my-2">
                    <h4>League Year:</h4>
                    <Select
                        isSearchable={false}
                        isClearable={false}
                        isMulti={false}
                        defaultValue={years
                            .map(y => ({ value: y, label: y }))
                            .find(opt => opt.value === currentYear)}
                        options={years.map(y => {
                            return {
                                value: y,
                                label: y,
                            };
                        })}
                        onChange={y => {
                            handleYearChange(y && y?.value);
                        }}
                    />
                </Col>
            </Row>
            <Row className="mt-2">
                <Col className="totals" xs={6} lg={9}>
                    <h4>Total: {currencyFormat.format(totals.total)}</h4>
                    <h4>Unpaid: {currencyFormat.format(totals.unpaid)}</h4>
                    <h4>Number: {totals.count}</h4>
                </Col>
                <Col className="d-flex justify-content-end align-self-end">
                    <CSVLink
                        data={csvData}
                        headers={CSVHeaders}
                        filename={`league-registrations.${new Date().getTime()}.csv`}
                        className="btn btn-primary"
                        target="_blank"
                        onClick={handleDownload}
                    >
                        Export Registration Data
                    </CSVLink>
                </Col>
            </Row>
            <Table
                className="leagueRegistrationsTable mt-2"
                striped
                bordered
                responsive
            >
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Paid Date</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Team Name</th>
                        <th>Age Level</th>
                        <th>Amount</th>
                        <th>Payment Type</th>
                        <th>View</th>
                    </tr>
                </thead>
                <tbody>
                    {orders
                        .filter(o =>
                            filters.ageLevels.filter
                                ? o.products.some(p =>
                                      filters.ageLevels.filter?.includes(
                                          p.name,
                                      ),
                                  )
                                : true,
                        )
                        .filter(o =>
                            filters.league.filter
                                ? o.products.some(
                                      p =>
                                          filters.league.filter?.label ===
                                          p.season,
                                  )
                                : true,
                        )
                        .filter(o =>
                            filters.statuses.filter
                                ? filters.statuses.filter.includes(o.status)
                                : true,
                        )
                        .filter(o => {
                            return filters.orderDateStart
                                ? new Date(
                                      defaultDateFormat.format(
                                          new Date(o.date),
                                      ),
                                  ) >=
                                      new Date(
                                          defaultDateFormat.format(
                                              filters.orderDateStart,
                                          ),
                                      )
                                : true;
                        })
                        .filter(o => {
                            return filters.orderDateEnd
                                ? new Date(
                                      defaultDateFormat.format(
                                          new Date(o.date),
                                      ),
                                  ) <=
                                      new Date(
                                          defaultDateFormat.format(
                                              filters.orderDateEnd,
                                          ),
                                      )
                                : true;
                        })
                        .filter(o => {
                            return filters.orderCompletionStart
                                ? o.completionDate
                                    ? new Date(
                                          defaultDateFormat.format(
                                              new Date(o.completionDate),
                                          ),
                                      ) >=
                                      new Date(
                                          defaultDateFormat.format(
                                              filters.orderCompletionStart,
                                          ),
                                      )
                                    : false
                                : true;
                        })
                        .filter(o => {
                            return filters.orderCompletionEnd
                                ? o.completionDate
                                    ? new Date(
                                          defaultDateFormat.format(
                                              new Date(o.completionDate),
                                          ),
                                      ) <=
                                      new Date(
                                          defaultDateFormat.format(
                                              filters.orderCompletionEnd,
                                          ),
                                      )
                                    : false
                                : true;
                        })
                        .filter(o =>
                            filters.user
                                ? o.user.email
                                      .toLowerCase()
                                      .includes(filters.user.toLowerCase()) ||
                                  o.user.name
                                      .toLowerCase()
                                      .includes(filters.user.toLowerCase())
                                : true,
                        )
                        .filter(o =>
                            filters.team
                                ? o.products.some(p =>
                                      p.meta.teamName
                                          ? p.meta.teamName
                                                .toLowerCase()
                                                .includes(
                                                    filters.team!.toLowerCase(),
                                                )
                                          : false,
                                  )
                                : true,
                        )
                        .sort((a, b) => {
                            return (
                                new Date(b.date).getTime() -
                                new Date(a.date).getTime()
                            );
                        })
                        .map(o =>
                            o.products
                                ? o.products
                                      .filter(p =>
                                          filters.ageLevels.filter
                                              ? filters.ageLevels.filter?.includes(
                                                    p.name,
                                                )
                                              : true,
                                      )
                                      .filter(p =>
                                          filters.league.filter
                                              ? filters.league.filter.label ===
                                                p.season
                                              : true,
                                      )
                                      .filter(p =>
                                          filters.team
                                              ? p.meta.teamName
                                                  ? p.meta.teamName
                                                        .toLowerCase()
                                                        .includes(
                                                            filters.team!.toLowerCase(),
                                                        )
                                                  : false
                                              : true,
                                      )
                                      .filter(p => p.status !== "cancelled")
                                      .map((p, idx) =>
                                          generateTableRow(o, p, idx),
                                      )
                                : generateTableRow(o),
                        )}
                </tbody>
            </Table>
        </section>
    ) : (
        <Outlet />
    );
};

export default LeagueRegistrations;
